import React, {
  useCallback,
  useMemo,
  useState,
  useEffect,
} from 'react';
import { useSelector } from 'react-redux';
import { Timeline } from 'antd';
import {
  CheckCircleOutlined,
  CoffeeOutlined,
  DeleteOutlined,
  EditOutlined,
  FormOutlined,
  LoginOutlined,
  LogoutOutlined,
  PlusCircleOutlined,
  ToolOutlined,
  UserSwitchOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { FormHelpers } from 'ontraccr-common';
import axios from 'axios';
import { DateTime } from 'luxon';

import { request } from '../../helpers/requests';
import { getIdMap, toTitleCase } from '../../helpers/helpers';
import Step from '../../common/containers/TimelineStep';
import { parseMetadata } from '../../forms/CompletedForms/FormTimeline';
import FormTimelineCardLink from '../../forms/CompletedForms/FormTimelineCardLink';
import { getApprovalRoutingTimelineText } from '../timecardApprovalHistory.helpers';

const FORM_STEP_TYPES = new Set(['formCreated', 'formUpdated']);
export default function TimeCardTimeline({
  entries,
  onCardLinkClick,
}) {
  const { t } = useTranslation();
  const users = useSelector((state) => state.users.users);
  const customers = useSelector((state) => state.customers.customers);
  const projects = useSelector((state) => state.projects.projects);
  const positionNames = useSelector((state) => state.settings.positionNames);

  const userMap = useMemo(() => getIdMap(users), [users]);
  const projectMap = useMemo(() => getIdMap(projects), [projects]);
  const positionMap = useMemo(() => getIdMap(positionNames), [positionNames]);
  const [cardBoardMap, setCardBoardMap] = useState({});

  const addCountToTitle = useCallback((title, count) => {
    if (count) {
      return `${title} (${count})`;
    }

    return title;
  }, []);

  const generateFormTimelineData = useCallback((type, metadata) => {
    const {
      formNumber,
      formName,
      submitter,
      projects: linkedProjects = [],
      cardName,
      cardId,
      customers: linkedCustomers = [],
      count,
    } = metadata;

    const projectNames = linkedProjects?.map((projectId) => projectMap[projectId]?.name).filter(Boolean).join(', ') ?? '';
    const customerNames = linkedCustomers?.map((customerId) => projectMap[customerId]?.name).filter(Boolean).join(', ') ?? '';

    const user = userMap[submitter]?.name || 'Unknown User';
    const formattedName = FormHelpers.formatFormName({
      number: formNumber,
      templateName: formName,
    });

    const relevantCount = count || 1;

    const prefix = type === 'formCreated' ? 'crea' : 'upda';

    const title = `Timecard ${toTitleCase(prefix)}ted from Form`;
    const text = `${user} submitted ${formattedName} ${prefix}ting ${relevantCount} ${relevantCount === 1 ? 'entry' : 'entries'}`;
    const children = (projectNames.length || customerNames.length || cardName) ? (
      <div style={{ fontSize: 12 }}>
        <strong>Form Details</strong>
        <ul>
          {!!projectNames.length && (
            <li>
              <strong>Projects: </strong>
              {projectNames}
            </li>
          )}
          {!!customerNames.length && (
            <li>
              <strong>Customers: </strong>
              {customerNames}
            </li>
          )}
          {!!cardName && (
            <li>
              <strong>Card: </strong>
              <FormTimelineCardLink
                cardTitle={cardName}
                cardId={cardId}
                boardId={cardBoardMap?.[cardId]}
                onCardLinkClick={onCardLinkClick}
              />
            </li>
          )}
        </ul>
      </div>
    ) : null;

    return {
      title,
      text,
      children,
    };
  }, [userMap, customers, projectMap, cardBoardMap, onCardLinkClick]);

  const getApprovalRoutingText = useCallback((step, metadata) => (
    getApprovalRoutingTimelineText({
      metadata,
      livePendingApprovers: step.livePendingApprovers,
      userMap,
      positionMap,
      t,
    })
  ), [userMap, positionMap, t]);

  const parseStep = useCallback((step) => {
    const userText = userMap[step.updatedByUserId]?.name || 'Unknown User';
    const count = step.count || 0;

    const metadata = parseMetadata(step.metadata);
    const approvalRoutingText = getApprovalRoutingText(step, metadata);

    switch (step.type) {
      case 'formCreated':
      case 'created': {
        let title = 'Timecard Created';
        let text = `${userText} created this timecard`;
        let children = null;

        if (step.type === 'formCreated') {
          const generatedData = generateFormTimelineData(step.type, {
            ...metadata,
            count,
          });

          title = generatedData.title;
          text = generatedData.text;
          children = generatedData.children;
        }

        return (
          <Step
            key={step.id}
            Icon={PlusCircleOutlined}
            title={addCountToTitle(title, count)}
            text={text}
            timestamp={step.timestamp}
          >
            {children}
          </Step>
        );
      }
      case 'formUpdated':
      case 'updated': {
        let title = 'Timecard Updated';
        let text = `${userText} updated this timecard`;
        let children = null;

        if (step.type === 'formUpdated') {
          const generatedData = generateFormTimelineData(step.type, {
            ...metadata,
            count,
          });

          title = generatedData.title;
          text = generatedData.text;
          children = generatedData.children;
        } else if (metadata?.field === 'startTime' && metadata?.oldStartTime && metadata?.newStartTime) {
          const zone = metadata.timezone;
          const formatTime = (ms) => {
            const dt = zone
              ? DateTime.fromMillis(ms, { zone })
              : DateTime.fromMillis(ms);
            return dt.toFormat('h:mm a');
          };
          text = `${userText} updated clock in time from ${formatTime(metadata.oldStartTime)} to ${formatTime(metadata.newStartTime)}`;
        }

        return (
          <Step
            key={step.id}
            Icon={EditOutlined}
            title={addCountToTitle(title, count)}
            text={text}
            timestamp={step.timestamp}
          >
            {children}
          </Step>
        );
      }
      case 'deleted':
        return (
          <Step
            key={step.id}
            Icon={DeleteOutlined}
            title={addCountToTitle('Timecard Deleted', count)}
            text={`${userText} deleted this timecard`}
            timestamp={step.timestamp}
          />
        );
      case 'submitted': {
        const submitText = approvalRoutingText
          ? `${userText} submitted this timecard. ${approvalRoutingText}`
          : `${userText} submitted this timecard`;
        return (
          <Step
            key={step.id}
            Icon={FormOutlined}
            title={addCountToTitle('Timecard Submitted', count)}
            text={submitText}
            timestamp={step.timestamp}
          />
        );
      }
      case 'approved': {
        const isForwarded = metadata?.forwarded === true;
        let approveText = `${userText} approved this timecard`;
        if (isForwarded) {
          approveText = approvalRoutingText
            ? `${userText} approved this timecard (level ${metadata.completedStage}). ${approvalRoutingText}`
            : `${userText} approved this timecard (level ${metadata.completedStage})`;
        }
        return (
          <Step
            key={step.id}
            color="green"
            Icon={CheckCircleOutlined}
            title={addCountToTitle(
              isForwarded ? 'Timecard Partially Approved' : 'Timecard Approved',
              count,
            )}
            text={approveText}
            timestamp={step.timestamp}
          />
        );
      }
      case 'clockedIn':
        return (
          <Step
            key={step.id}
            Icon={LoginOutlined}
            title={addCountToTitle('Clocked In', count)}
            text={`${userText} clocked in`}
            timestamp={step.timestamp}
          />
        );
      case 'clockedOut': {
        const clockOutMeta = parseMetadata(step.metadata);
        const isAutoClockedOut = clockOutMeta?.isAutoClockOut === true;
        return (
          <Step
            key={step.id}
            Icon={LogoutOutlined}
            title={addCountToTitle(isAutoClockedOut ? 'Auto Clocked Out' : 'Clocked Out', count)}
            text={isAutoClockedOut
              ? 'User was automatically clocked out after exceeding the maximum clock-in duration'
              : `${userText} clocked out`}
            timestamp={step.timestamp}
          />
        );
      }
      case 'breakStart':
        return (
          <Step
            key={step.id}
            Icon={CoffeeOutlined}
            title={addCountToTitle('Break Started', count)}
            text={`${userText} started a break`}
            timestamp={step.timestamp}
          />
        );
      case 'breakEnd':
        return (
          <Step
            key={step.id}
            Icon={ToolOutlined}
            title={addCountToTitle('Break Ended', count)}
            text={`${userText} ended a break`}
            timestamp={step.timestamp}
          />
        );
      case 'workAfterBreak':
        return (
          <Step
            key={step.id}
            Icon={LoginOutlined}
            title={addCountToTitle('Work After Break', count)}
            text={`${userText} clocked in`}
            timestamp={step.timestamp}
          />
        );
      case 'serviceAfterBreak':
        return (
          <Step
            key={step.id}
            Icon={LoginOutlined}
            title={addCountToTitle('Service After Break', count)}
            text={`${userText} clocked in`}
            timestamp={step.timestamp}
          />
        );
      case 'clockedInSwitch':
        return (
          <Step
            key={step.id}
            Icon={UserSwitchOutlined}
            title={addCountToTitle('Shift Switch Clock In', count)}
            text={`${userText} clocked in`}
            timestamp={step.timestamp}
          />
        );
      case 'clockedOutSwitch':
        return (
          <Step
            key={step.id}
            Icon={UserSwitchOutlined}
            title={addCountToTitle('Shift Switch Clock Out', count)}
            text={`${userText} clocked out`}
            timestamp={step.timestamp}
          />
        );
      case 'createdOffline':
        return (
          <Step
            key={step.id}
            Icon={PlusCircleOutlined}
            title={addCountToTitle('Timecard Created Offline', count)}
            text={`${userText} created this timecard offline`}
            timestamp={step.timestamp}
          />
        );
      case 'updatedOffline':
        return (
          <Step
            key={step.id}
            Icon={EditOutlined}
            title={addCountToTitle('Timecard Updated Offline', count)}
            text={`${userText} updated this timecard offline`}
            timestamp={step.timestamp}
          />
        );
      case 'rejected':
        return (
          <Step
            key={step.id}
            color="red"
            Icon={CloseCircleOutlined}
            title={addCountToTitle('Timecard Rejected', count)}
            text={`${userText} rejected this timecard`}
            timestamp={step.timestamp}
            note={step.note}
          />
        );
      case 'qboSyncSucceeded':
        return (
          <Step
            key={step.id}
            color="green"
            Icon={CheckCircleOutlined}
            title={addCountToTitle('QuickBooks Sync Succeeded', count)}
            text="Time entry synced to QuickBooks"
            timestamp={step.timestamp}
          />
        );
      case 'qboSyncFailed':
        return (
          <Step
            key={step.id}
            color="red"
            Icon={CloseCircleOutlined}
            title={addCountToTitle('QuickBooks Sync Failed', count)}
            text="Failed to sync time entry to QuickBooks"
            timestamp={step.timestamp}
            note={step.note}
          />
        );
      default:
        return null;
    }
  }, [userMap, addCountToTitle, generateFormTimelineData, getApprovalRoutingText]);

  const cardIds = useMemo(() => (
    entries.filter((entry) => FORM_STEP_TYPES.has(entry.type))
      .map((entry) => {
        const { metadata } = entry;
        const parsedMetadata = parseMetadata(metadata);
        const { cardId } = parsedMetadata ?? {};
        return cardId;
      }).filter(Boolean)
  ), [entries]);

  useEffect(() => {
    const loadBoardForCards = async () => {
      const { data } = await request({
        call: axios.get('/boards/cards/getBoardForCards', {
          params: {
            ids: cardIds,
          },
        }),
        suppressError: true,
        hideSuccessToast: true,
      });
      setCardBoardMap(data ?? {});
    };
    if (cardIds?.length) {
      loadBoardForCards();
    } else {
      setCardBoardMap({});
    }
  }, [cardIds]);

  return (
    <div>
      <Timeline>
        {(entries ?? []).map(parseStep)}
      </Timeline>
    </div>
  );
}

TimeCardTimeline.propTypes = {
  entries: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    timestamp: PropTypes.string.isRequired,
    updatedByUserId: PropTypes.string.isRequired,
    count: PropTypes.number,
  })),
  onCardLinkClick: PropTypes.func,
};

TimeCardTimeline.defaultProps = {
  entries: [],
  onCardLinkClick: undefined,
};
